import React, { useEffect } from "react";
import {
  makeBannerProps,
  StandardPageProps,
  makeStandardPageProps,
  useNextRouterPathnameWithQuery,
  makeHtml,
  getLocationsInUrl,
} from "../utils/common";
import { useRouter } from "next/router";
import BannerComponent from "./mapped-templates/BannerTemplate";
import { BannerWidgetContainer } from "./mapped-templates/BannerWidgetContainerTemplate";
import HeaderComponentTemplate from "./mapped-templates/HeaderTemplate";
import PageProperties from "./PagePropertiesTemplate";
import { CssBaseline } from "@mui/material";
import PrimaryContent from "../utils/PrimaryContent";
import { assignFeefoReviewAPITag, checkTradingTabExpiry } from "../utils";
import FooterComponentTemplate from "./mapped-templates/FooterTemplate";
import ContentArea from "../utils/ContentArea";
import {
  useIsAuthenticated,
  useIsAuthenticating,
  useLocationsSelector,
  useLoginPageUrlSelector,
} from "@/store/selectors";
import { MobileTradingTab } from "@components/SemanticTheme/MobileTradingTab";
import {
  CabinDetailsPageBannerComponentDynamicZone,
  CabinDetailsPageEntityResponseCollection,
  FooterEntityResponseCollection,
  HeaderEntityResponseCollection,
  SeoStructuredDataEntityResponse,
} from "src/graphql/generated-strapi/types";
import { ParsedUrlQuery } from "querystring";
import { updateAbsoluteLink } from "@components/Utils";
import { setSelectedLocationIds } from "@/store/actions";
import { useDispatch } from "react-redux";

interface CabinDetailsPageProps {
  rest: {
    strapiContent: {
      data: { cabinDetailsPages: CabinDetailsPageEntityResponseCollection };
    };
    experiments?: { [key in string]: string | boolean };
    headerContent: {
      data: { headers: HeaderEntityResponseCollection };
    };
    footerContent: {
      data: { footers: FooterEntityResponseCollection };
    };
    seoStructuredData: {
      data: {
        seoStructuredData: SeoStructuredDataEntityResponse;
      };
    };
    hideSubscribeSticky?: boolean;
    queryData?: ParsedUrlQuery;
    headers?: any;
  };
}

const CabinDetailsPageComponent = (props: CabinDetailsPageProps) => {
  const experiments = props?.rest?.experiments;
  const seoStructuredData =
    props?.rest?.seoStructuredData?.data?.seoStructuredData?.data?.attributes;
  const cabinDetailsPageCmsData =
    props?.rest?.strapiContent?.data?.cabinDetailsPages?.data?.[0]?.attributes;
  const pageImgQuality = cabinDetailsPageCmsData?.ImageQuality;
  const bannerProps = makeBannerProps(cabinDetailsPageCmsData || {});
  const tradingTabProps = cabinDetailsPageCmsData?.TradingTab?.data?.attributes;
  const headerProps =
    props?.rest?.headerContent?.data?.headers?.data?.[0]?.attributes;
  const footerProps =
    props?.rest?.footerContent?.data?.footers?.data?.[0]?.attributes;
  const primaryContent = cabinDetailsPageCmsData?.PrimaryContent;
  const bannerComponentRef =
    cabinDetailsPageCmsData?.BannerComponent as CabinDetailsPageBannerComponentDynamicZone[];
  const isBookingWidget =
    bannerComponentRef && !!Object.keys(bannerComponentRef)?.length;
  const stickyBannerProps =
    cabinDetailsPageCmsData?.StickyBanner?.data?.attributes || null;
  const pageProperties: StandardPageProps = makeStandardPageProps(
    cabinDetailsPageCmsData?.seo || {},
  );
  pageProperties["ShowSubscribeModal"] =
    cabinDetailsPageCmsData?.ShowSubscribeModal;
  assignFeefoReviewAPITag(pageProperties, cabinDetailsPageCmsData);
  const faqComponentContent = cabinDetailsPageCmsData?.PrimaryContent?.filter(
    (item) => item?.hasOwnProperty("faq_comp"),
  );
  pageProperties["faqComponentContent"] = faqComponentContent;
  if (stickyBannerProps && headerProps)
    headerProps["stickyBannerProps"] = stickyBannerProps;
  if (pageImgQuality && headerProps)
    headerProps["pageImgQuality"] = pageImgQuality;
  if (props?.rest?.headers) pageProperties["nextHeaders"] = props.rest.headers;
  if (!!seoStructuredData)
    pageProperties["seoStructuredData"] = seoStructuredData;

  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const loginUrl = useLoginPageUrlSelector();
  const { bookableLocations } = useLocationsSelector();

  const dispatch = useDispatch();
  const history = useRouter();

  useEffect(() => {
    if (bookableLocations?.length > 0) {
      const locationsInUrl = getLocationsInUrl(bookableLocations);
      if (!!locationsInUrl?.length) {
        const locationIds = locationsInUrl.map((location) => location?.id);
        if (locationIds?.length > 0)
          dispatch(setSelectedLocationIds(locationIds));
      } else {
        dispatch(setSelectedLocationIds([]));
      }
    }
  }, [bookableLocations]);

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticating) {
      const pathname = useNextRouterPathnameWithQuery(history);
      if (cabinDetailsPageCmsData?.AccountAuthenticated)
        !!loginUrl && history.push(`${loginUrl}?returnUrl=${pathname}`);
      return;
    }
  }, [isAuthenticated, isAuthenticating, loginUrl]);

  const parsedText = (text?: string) => {
    return text ? (
      <div dangerouslySetInnerHTML={makeHtml(text as string)} />
    ) : undefined;
  };

  let nextHeaders: any;
  if (!!props?.rest?.headers) {
    nextHeaders = JSON.parse(props?.rest?.headers);
  }

  return (
    <>
      <PageProperties {...pageProperties} />
      <CssBaseline />
      {bannerProps && (
        <BannerComponent
          {...bannerProps}
          isBookingWidget={isBookingWidget}
          queryData={props?.rest?.queryData}
        >
          {headerProps && (
            <HeaderComponentTemplate
              content={headerProps}
              additionalProps={experiments}
              hideSubscribeSticky={props?.rest?.hideSubscribeSticky}
            />
          )}
          {tradingTabProps?.TradingTabInMobile &&
            checkTradingTabExpiry(bannerProps) && (
              <MobileTradingTab
                tradingTab={{
                  url: updateAbsoluteLink(
                    bannerProps?.tradingTabURL?.data?.attributes?.PageRoute,
                  ),
                  tradingTabInMobile: parsedText(
                    tradingTabProps?.TradingTabInMobile,
                  ),
                }}
              />
            )}
          {bannerComponentRef && isBookingWidget && (
            <BannerWidgetContainer>
              <ContentArea
                content={bannerComponentRef}
                additionalProps={experiments}
              />
            </BannerWidgetContainer>
          )}
        </BannerComponent>
      )}
      {primaryContent && (
        <PrimaryContent
          primaryContent={primaryContent}
          pageImgQuality={cabinDetailsPageCmsData?.ImageQuality}
          additionalProps={experiments}
        />
      )}
      {footerProps && (
        <FooterComponentTemplate
          content={{
            ...footerProps,
            experiments,
          }}
        />
      )}
    </>
  );
};

export default CabinDetailsPageComponent;
